import React, {
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import format from 'string-template';
import PropTypes from 'prop-types';

import AnnouncementContext from '../../../../context/AnnouncementContext';
import ExternalCoachContext from '../../../../context/ExternalCoachContext';
import LoggedInUserContext from '../../../../../context/LoggedInUserContext';
import useToast from '../../../../hooks/useToast';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import AnnouncementComment from '../../../../Model/AnnouncementComment';
import AnnouncementItem from '../AnnouncementList/components/AnnouncementItem';
import CommentItem from '../CommentItem';
import { getAnnouncementCommentCount, getCommunityLabel } from '../../../../utils/announcement';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import LoadingPage from '../../../../../components/LoadingPage';
import { PrimaryButton } from '../../../../../components/Button/ActionButtons';
import { FLASH_CLASSNAME, FLASH_SECONDS } from '../CommentItem/styles';

import CommentInput from './components/CommentInput';
import {
  FullPostContainer,
  AnnouncementContainer,
  PodFilterContainer,
  InputContainer,
  EditingBar,
  EditingText,
  StyledCancelIcon,
  CommentsContainer,
  StyledDialog,
  EmptyCommentsContainer,
  CharacterCountContainer,
  CharacterCountText,
  WarningText,
  SectionLabel,
  RowList,
  CommentsRow,
  CommunityRail,
  CommentsColumn,
  CommunityRow,
  CommunityRowLabel,
  CommunityRowCount,
} from './styles';
import texts from './texts.json';

const SCROLL_TO_NEW_COMMENT_DELAY = 100;
const SCROLL_TO_COMMENT_DELAY = 800;
const FLASH_ANIMATION_DELAY = 300;
const FOCUS_INPUT_DELAY = 100;
const CHARACTER_LIMIT = 500;

const FullPostView = ({ isOpen, onClose }) => {
  const [reply, setReply] = useState('');
  const [editingComment, setEditingComment] = useState(null);
  const [isAddingReply, setIsAddingReply] = useState(false);

  const {
    selectedAnnouncement,
    isCommentsReady,
    selectedCommentId,
    podFilterOptions,
    showPodFilter,
    selectedPodFilterId,
    setSelectedPodFilterId,
    filteredComments,
    // Defaulted: the rail is optional, and a consumer may provide a context without the group.
    selectedGroupSiblings = [],
    selectedSiblingId,
    selectGroupSibling,
    isSiblingSwitching,
    communities = [],
  } = useContext(AnnouncementContext);

  const { coachDoc } = useContext(ExternalCoachContext);
  const { userId: loggedInUserId } = useContext(LoggedInUserContext);
  const { showToast } = useToast();

  const replyInputRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const commentsContainerRef = useRef(null);
  const isComponentMountedRef = useComponentMounted();

  useEffect(() => {
    let flashTimeoutId;
    let scrollTimeoutId;

    if (selectedCommentId && isCommentsReady) {
      // Add a small delay to ensure DOM elements are rendered
      scrollTimeoutId = setTimeout(() => {
        const commentElement = document.getElementById(selectedCommentId);
        if (commentElement && scrollContainerRef.current) {
          // The post and comments now share a single scroll container, so measure the
          // comment's position relative to that container rather than assuming it's the
          // comment element's offsetParent.
          const containerRect = scrollContainerRef.current.getBoundingClientRect();
          const commentRect = commentElement.getBoundingClientRect();
          const scrollTop = scrollContainerRef.current.scrollTop
            + (commentRect.top - containerRect.top);

          // Scroll within the full post container (NOT the whole page)
          scrollContainerRef.current.scrollTo({
            top: scrollTop,
            behavior: 'smooth',
          });
          // Add flash animation after a small delay to ensure scroll has started
          setTimeout(() => {
            commentElement.classList.add(FLASH_CLASSNAME);
            // Set timeout to remove flash class after the animation duration
            flashTimeoutId = setTimeout(() => {
              if (commentElement.classList.contains(FLASH_CLASSNAME)) {
                commentElement.classList.remove(FLASH_CLASSNAME);
              }
            }, FLASH_SECONDS * 1000);
          }, FLASH_ANIMATION_DELAY);
        }
      }, SCROLL_TO_COMMENT_DELAY);

      return () => {
        clearTimeout(scrollTimeoutId);
        if (flashTimeoutId) clearTimeout(flashTimeoutId);
      };
    }
    /**
     * Cleanup function: remove any active flash animations and clear pending timeouts
     * to prevent memory leaks and unwanted side effects on component unmount
     */
    return () => {
      if (selectedCommentId) {
        const commentElement = document.getElementById(selectedCommentId);
        if (commentElement) {
          commentElement.classList.remove(FLASH_CLASSNAME);
        }
      }
      if (flashTimeoutId) clearTimeout(flashTimeoutId);
      if (scrollTimeoutId) clearTimeout(scrollTimeoutId);
    };
  }, [
    selectedCommentId,
    isCommentsReady,
  ]);

  const isReplyOverLimit = reply.length > CHARACTER_LIMIT;

  // Comment edits and drafts belong to the community whose thread they were started in. Switching
  // rows swaps the thread under them, so submitting would rewrite a comment - or post a reply -
  // in a community that is no longer on screen. Drop both when the selection moves.
  useEffect(() => {
    setEditingComment(null);
    setReply('');
  }, [selectedSiblingId]);

  // The coach's selection order, taken off the base sibling the same way the feed card takes it.
  // Siblings share one `createdAt`, so the order getGroupSiblings returns is arbitrary - without
  // this the rail and the card's chips list the same communities differently.
  const groupCommunityIds = useMemo(() => {
    const base = selectedGroupSiblings.find((sibling) => sibling.id === sibling.basePostId)
      || selectedGroupSiblings[0];
    return base?.groupCommunityIds || [];
  }, [selectedGroupSiblings]);

  // A post sent to several communities keeps a thread per community, so the rail lists one row
  // each. The card and every comment control read `selectedAnnouncement`, so they follow the
  // selected community without further work.
  const communityRows = useMemo(() => {
    // A sibling whose community dropped out of the list sorts last rather than to the front.
    const orderOf = (communityId) => {
      const index = groupCommunityIds.indexOf(communityId);
      return index === -1 ? groupCommunityIds.length : index;
    };
    return selectedGroupSiblings
      .map((sibling) => ({
        id: sibling.id,
        communityId: sibling.communityId,
        label: getCommunityLabel(sibling.communityId, communities, sibling.communityName),
        count: getAnnouncementCommentCount(sibling),
      }))
      .sort((a, b) => orderOf(a.communityId) - orderOf(b.communityId));
  }, [selectedGroupSiblings, communities, groupCommunityIds]);

  const showCommunityRail = communityRows.length > 1;

  const onAddUpdateReply = useCallback(async (processedAttachments) => {
    try {
      setIsAddingReply(true);
      if (editingComment) {
        await editingComment.updateComment({
          comment: reply.trim(),
          updatedAt: new Date(),
          updatedBy: loggedInUserId,
        });
      } else {
        await AnnouncementComment.addComment(selectedAnnouncement.id, {
          comment: reply.trim(),
          createdAt: new Date(),
          isRemoved: false,
          coachId: selectedAnnouncement.coach,
          announcementId: selectedAnnouncement.id,
          userName: coachDoc.name,
          userAvatarUrl: coachDoc.avatar,
          userId: coachDoc.id,
          isByCoach: true,
          reactions: 0,
          addedBy: loggedInUserId,
          podId: selectedPodFilterId,
          ...(processedAttachments.length > 0 && { attachments: processedAttachments }),
        });
      }
      if (isComponentMountedRef.current) {
        setReply('');
        setEditingComment(null);
        setTimeout(() => {
          const container = scrollContainerRef.current;
          const comments = commentsContainerRef.current;
          if (container && comments) {
            // Comments are sorted newest-first, so the comment just added/edited is at
            // the top of the list. Scroll to the comment list's own offset (not top: 0
            // of the whole container), so a tall post stays scrolled past instead of
            // the reply scrolling all the way back up to the top of the post.
            const containerRect = container.getBoundingClientRect();
            const commentsRect = comments.getBoundingClientRect();
            container.scrollTo({
              top: container.scrollTop + (commentsRect.top - containerRect.top),
              behavior: 'smooth',
            });
          }
        }, SCROLL_TO_NEW_COMMENT_DELAY);
      }
      showToast(editingComment ? texts.updateCommentSuccess : texts.addCommentSuccess);
    } catch (error) {
      showToast(format(editingComment ? texts.updateCommentError : texts.addCommentError, { error: error.message }), {
        error: true,
      });
    } finally {
      if (isComponentMountedRef.current) {
        setIsAddingReply(false);
      }
    }
  }, [
    selectedAnnouncement,
    reply,
    isComponentMountedRef,
    editingComment,
    coachDoc,
    loggedInUserId,
    showToast,
    selectedPodFilterId,
  ]);

  const onClickEditComment = useCallback((selectedComment) => {
    setReply(selectedComment.comment);
    setEditingComment(selectedComment);
    // focus the input element after the reply is set in the input field
    setTimeout(() => {
      const inputElement = replyInputRef.current?.querySelector('input');
      inputElement?.focus();
    }, FOCUS_INPUT_DELAY);
  }, []);

  const onCancelEditComment = useCallback(() => {
    setEditingComment(null);
    setReply('');
  }, []);

  const renderCommentsContent = useMemo(() => {
    if (filteredComments.length === 0) {
      return <EmptyCommentsContainer>{texts.noComments}</EmptyCommentsContainer>;
    }
    return filteredComments.map((comment) => (
      <CommentItem key={comment.id} comment={comment} onClickEditComment={onClickEditComment} />
    ));
  }, [filteredComments, onClickEditComment]);

  return (
    <StyledDialog open={isOpen} onClose={onClose} fullWidth maxWidth={showCommunityRail ? 'lg' : 'md'}>
      <FullPostContainer
        $isLoadingView={!isCommentsReady}
        ref={scrollContainerRef}
        data-testid="full-post-scroll-container"
      >
        {!isCommentsReady ? (
          <LoadingPage />
        ) : (
          <>
            <AnnouncementContainer>
              <AnnouncementItem announcement={selectedAnnouncement} isFullPostView />
            </AnnouncementContainer>
            <CommentsRow>
              {showCommunityRail && (
                <CommunityRail>
                  <SectionLabel>{texts.communityRailLabel}</SectionLabel>
                  <RowList>
                    {communityRows.map(({ id, label, count }) => {
                      const active = selectedSiblingId === id;
                      return (
                        <CommunityRow
                          key={id}
                          type="button"
                          $active={active}
                          onClick={() => selectGroupSibling(id)}
                          aria-pressed={active}
                        >
                          <CommunityRowLabel>{label}</CommunityRowLabel>
                          <CommunityRowCount>{count}</CommunityRowCount>
                        </CommunityRow>
                      );
                    })}
                  </RowList>
                </CommunityRail>
              )}
              <CommentsColumn>
                {showPodFilter && (
                  <PodFilterContainer>
                    {Object.entries(podFilterOptions).map(([optionId, optionLabel]) => (
                      <PrimaryButton
                        key={optionId}
                        onClick={() => setSelectedPodFilterId(optionId)}
                        variant={selectedPodFilterId === optionId ? 'dark' : 'info'}
                        size="medium"
                        $fitToContent
                      >
                        {optionLabel}
                      </PrimaryButton>
                    ))}
                  </PodFilterContainer>
                )}
                <CommentsContainer ref={commentsContainerRef} data-testid="full-post-comments-container">
                  {/* Only the thread is reloading, so the post card and the rail stay put. */}
                  {isSiblingSwitching ? <LoadingPage /> : renderCommentsContent}
                </CommentsContainer>
                <InputContainer>
                  {!!editingComment && (
                    <EditingBar>
                      <EditingText>{`${texts.editingComment} ${editingComment.comment}`}</EditingText>
                      <StyledCancelIcon onClick={onCancelEditComment} />
                    </EditingBar>
                  )}
                  <CommentInput
                    text={reply}
                    onChange={setReply}
                    onSubmit={onAddUpdateReply}
                    isSendingComment={isAddingReply}
                    inputRef={replyInputRef}
                    coachDoc={coachDoc}
                    isReplyOverLimit={isReplyOverLimit}
                    isEditComment={!!editingComment}
                  />
                  <CharacterCountContainer $isOverLimit={isReplyOverLimit}>
                    {isReplyOverLimit && <WarningText>{texts.characterLimitWarning}</WarningText>}
                    <CharacterCountText>
                      {format(texts.characterCount, { current: reply.length, max: CHARACTER_LIMIT })}
                    </CharacterCountText>
                  </CharacterCountContainer>
                </InputContainer>
              </CommentsColumn>
            </CommentsRow>
            <LoadingOverlay isLoading={isAddingReply} />
          </>
        )}
      </FullPostContainer>
    </StyledDialog>
  );
};

FullPostView.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default compose(
  observer,
)(FullPostView);
